// src/pages/auth/Login.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../core/api';
import './Login.css';

const toEn = (s) =>
  String(s)
    .replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
    .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d))
    .trim();

const Login = () => {
  const navigate = useNavigate();

  const [mode, setMode] = useState('login'); // login | forgot-phone | forgot-code
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [failedLogin, setFailedLogin] = useState(false);

  const [code, setCode] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [cooldown, setCooldown] = useState(0);

  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  /* ============== ورود ============== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    try {
      const response = await api.post('/auth/login', {
        phoneNumber: username,
        password,
      });

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        const userResponse = await api.get('/auth/me');
        localStorage.setItem('user', JSON.stringify(userResponse.data));
        navigate('/');
      } else {
        setError('پاسخ سرور نامعتبر است.');
      }
    } catch (err) {
      if (err.response) {
        if (err.response.status === 401) setFailedLogin(true);
        setError(
          err.response.data?.error ||
            err.response.data?.message ||
            'نام کاربری یا رمز عبور اشتباه است.'
        );
      } else if (err.request) {
        setError('خطا در ارتباط با سرور. لطفاً دوباره تلاش کنید.');
      } else {
        setError('خطای ناشناخته رخ داد.');
      }
    } finally {
      setLoading(false);
    }
  };

  /* ============== فراموشی رمز ============== */
  const openForgot = () => {
    setError('');
    setInfo('');
    setMode('forgot-phone');
  };

  const backToLogin = () => {
    setError('');
    setInfo('');
    setCode('');
    setNewPass('');
    setConfirm('');
    setMode('login');
  };

  const sendCode = async () => {
    if (!username.trim()) return setError('شماره موبایل را وارد کنید');
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/forgot/request', { phoneNumber: username });
      setMode('forgot-code');
      setCooldown(60);
      setInfo('اگر این شماره متعلق به مالکِ متصل به بله باشد، کد تأیید به بله ارسال شد.');
    } catch (err) {
      setError(err.response?.data?.error || 'خطا در ارسال کد. دوباره تلاش کنید.');
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(toEn(code))) return setError('کد باید ۶ رقم باشد');
    if (newPass.length < 6) return setError('رمز جدید باید حداقل ۶ کاراکتر باشد');
    if (newPass !== confirm) return setError('تکرار رمز جدید مطابقت ندارد');

    setLoading(true);
    try {
      await api.post('/auth/forgot/reset', {
        phoneNumber: username,
        code: toEn(code),
        newPassword: newPass,
      });
      backToLogin();
      setPassword('');
      setFailedLogin(false);
      setInfo('رمز عبور تغییر کرد. با رمز جدید وارد شوید.');
    } catch (err) {
      setError(err.response?.data?.error || 'خطا در تغییر رمز عبور');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="logo-dot">ف</div>

        {mode === 'login' && (
          <>
            <h1>ورود به سیستم فاکتور</h1>
            <p className="sub">برای دسترسی به فاکتورها وارد شوید</p>

            {error && <div className="login-error">{error}</div>}
            {info && <div className="login-success">{info}</div>}

            <form onSubmit={handleSubmit}>
              <div className="field">
                <label>نام کاربری</label>
                <input
                  name="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoFocus
                  required
                />
              </div>
              <div className="field">
                <label>رمز عبور</label>
                <input
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <button className="btn" type="submit" disabled={loading}>
                {loading ? 'در حال ورود...' : 'ورود'}
              </button>
            </form>

            {failedLogin && (
              <button type="button" className="login-link" onClick={openForgot}>
                رمز عبور را فراموش کرده‌ام
              </button>
            )}
          </>
        )}

        {mode === 'forgot-phone' && (
          <>
            <h1>بازیابی رمز عبور</h1>
            <p className="sub">
              کد تأیید به حساب بله‌ی متصل به پنل ارسال می‌شود (فقط برای مالک)
            </p>

            {error && <div className="login-error">{error}</div>}

            <div className="field">
              <label>شماره موبایل</label>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                inputMode="tel"
                autoFocus
              />
            </div>
            <button className="btn" type="button" onClick={sendCode} disabled={loading}>
              {loading ? 'در حال ارسال...' : 'ارسال کد به بله'}
            </button>
            <button type="button" className="login-link" onClick={backToLogin}>
              بازگشت به ورود
            </button>
          </>
        )}

        {mode === 'forgot-code' && (
          <>
            <h1>تعیین رمز جدید</h1>
            <p className="sub">کد ارسال‌شده در بله و رمز جدید را وارد کنید</p>

            {error && <div className="login-error">{error}</div>}
            {info && <div className="login-success">{info}</div>}

            <form onSubmit={resetPassword}>
              <div className="field">
                <label>کد تأیید (۶ رقم)</label>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="one-time-code"
                  style={{ direction: 'ltr', textAlign: 'center', letterSpacing: 6 }}
                  autoFocus
                  required
                />
              </div>
              <div className="field">
                <label>رمز جدید</label>
                <input
                  type="password"
                  value={newPass}
                  onChange={(e) => setNewPass(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="field">
                <label>تکرار رمز جدید</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
              <button className="btn" type="submit" disabled={loading}>
                {loading ? 'در حال ذخیره...' : 'ذخیره رمز جدید'}
              </button>
            </form>

            <button
              type="button"
              className="login-link"
              onClick={sendCode}
              disabled={cooldown > 0 || loading}
            >
              {cooldown > 0
                ? `ارسال مجدد کد (${cooldown.toLocaleString('fa-IR')} ثانیه)`
                : 'ارسال مجدد کد'}
            </button>
            <button type="button" className="login-link" onClick={backToLogin}>
              بازگشت به ورود
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;